import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Button,
  Card,
  CardBody,
  Badge,
  Table,
} from "reactstrap";
import getTitle from "../../../utilities/getTitle";
import { formattedDateTime } from "../../../utilities/formatedDate";
import VisibilityIcon from '@mui/icons-material/Visibility';
import PlanDetailsModal from './plan-details-modal';
import * as XLSX from 'xlsx';
import { getUserbyId, getPartnershipById, getConsolidatorById } from "../../../api";

const TicketModal = (props) => {
  const ticketDetails = props.data;
  const module_id = JSON.parse(localStorage.getItem("user"))?.moduleMapInfo?.module_id;
  const [isCommonTicketModalOpen, setCommonTicketModalOpen] = useState(false);
  const [showPlanDetailsModal, setShowPlanDetailsModal] = useState(false);

  const parsedPayload = useMemo(() => {
    try {
      if (!ticketDetails?.payload) return null;
      let parsed = ticketDetails.payload;
      while (typeof parsed === 'string') {
          parsed = JSON.parse(parsed);
      }
      return parsed;
    } catch (e) {
      console.error("Payload parsing error:", e);
      return null;
    }
  }, [ticketDetails]);

  useEffect(() => {
    if (!ticketDetails?.type) {
      setCommonTicketModalOpen(true);
    }
  }, [ticketDetails]);

  const isMscChangeTicket = [
    "MSC_CHANGE_NH",
    "MSC_CHANGE_HO",
    "MSC_CHANGE_RM",
    "BULK_MSC_CHANGE_RM",
    "BULK_MSC_CHANGE_NH",
    "BULK_MSC_CHANGE_HO",
  ].includes(ticketDetails?.type);

  const isRoleReassignment = ticketDetails?.type === "ROLE_REASSIGNMENT";
  let roleReassignData = { roleKey: "-", oldUserId: "-", newUserId: "-", bpIds: [], baIds: [] };

  if (isRoleReassignment) {
      try {
          if (Array.isArray(parsedPayload?.accept) && parsedPayload.accept.length > 0) {
              const firstAccept = parsedPayload.accept[0];
              
              let updateCriteria = firstAccept?.updateCriteria;
              while (typeof updateCriteria === 'string') {
                  try { updateCriteria = JSON.parse(updateCriteria); } catch(e) { break; }
              }
              if (updateCriteria && typeof updateCriteria === 'object') {
                  const keys = Object.keys(updateCriteria).filter(k => k.includes("_id") && !k.includes("partnership") && !k.includes("consolidator"));
                  if (keys.length > 0) {
                      const oldVal = updateCriteria[keys[0]];
                      roleReassignData.oldUserId = String(oldVal && oldVal.$in ? oldVal.$in[0] : oldVal);
                  }
              }

              let bodyData = firstAccept?.Body;
              while (typeof bodyData === 'string') {
                  try { bodyData = JSON.parse(bodyData); } catch(e) { break; }
              }
              if (bodyData && typeof bodyData === 'object') {
                  const keys = Object.keys(bodyData);
                  if (keys.length > 0) {
                      roleReassignData.roleKey = String(keys[0]).replace("_id", "").toUpperCase();
                      const newVal = bodyData[keys[0]];
                      roleReassignData.newUserId = String(newVal && newVal.$in ? newVal.$in[0] : newVal);
                  }
              }

              parsedPayload.accept.forEach(item => {
                  let crit = item?.updateCriteria;
                  while (typeof crit === 'string') {
                      try { crit = JSON.parse(crit); } catch(e) { break; }
                  }
                  if (crit?.partnership_id) {
                      let pId = crit.partnership_id;
                      if (pId && pId.$in) pId = pId.$in;
                      roleReassignData.bpIds = Array.isArray(pId) ? pId : [pId];
                  }
                  if (crit?.consolidator_id) {
                      let cId = crit.consolidator_id;
                      if (cId && cId.$in) cId = cId.$in;
                      roleReassignData.baIds = Array.isArray(cId) ? cId : [cId];
                  }
              });
          }
      } catch (err) {
          console.error("Error parsing role reassignment payload", err);
      }
  }

  const [roleReassignNames, setRoleReassignNames] = useState({ oldUser: null, newUser: null, bpNames: [], baNames: [] });

  useEffect(() => {
    if (isRoleReassignment) {
        if (roleReassignData.oldUserId && roleReassignData.oldUserId !== "-") {
            getUserbyId(roleReassignData.oldUserId).then(res => {
                if (res?.data?.result) setRoleReassignNames(prev => ({ ...prev, oldUser: res.data.result.name }));
            }).catch(() => {});
        }
        if (roleReassignData.newUserId && roleReassignData.newUserId !== "-") {
            getUserbyId(roleReassignData.newUserId).then(res => {
                if (res?.data?.result) setRoleReassignNames(prev => ({ ...prev, newUser: res.data.result.name }));
            }).catch(() => {});
        }
        if (roleReassignData.bpIds.length > 0) {
            Promise.all(roleReassignData.bpIds.map(id => getPartnershipById(id))).then(responses => {
                const names = responses.map(res => res?.data?.result?.name).filter(Boolean);
                setRoleReassignNames(prev => ({ ...prev, bpNames: names }));
            }).catch(() => {});
        }
        if (roleReassignData.baIds.length > 0) {
            Promise.all(roleReassignData.baIds.map(id => getConsolidatorById(id))).then(responses => {
                const names = responses.map(res => res?.data?.result?.name).filter(Boolean);
                setRoleReassignNames(prev => ({ ...prev, baNames: names }));
            }).catch(() => {});
        }
    }
  }, [isRoleReassignment, roleReassignData.oldUserId, roleReassignData.newUserId, JSON.stringify(roleReassignData.bpIds), JSON.stringify(roleReassignData.baIds)]);

  const planDetails = useMemo(() => {
    const details = parsedPayload?.accept?.[0]?.additionalFields?.planDetails;
    return Array.isArray(details) ? details : null;
  }, [parsedPayload]);

  // Normalize change/old objects for CASHIFY_POLICY_EDIT if present
  const changeObj =
    parsedPayload && parsedPayload.change_values && typeof parsedPayload.change_values === "object"
      ? parsedPayload.change_values
      : null;
  const oldObj =
    parsedPayload && parsedPayload.old_values && typeof parsedPayload.old_values === "object"
      ? parsedPayload.old_values
      : null;

  // helper to compare values (handles objects & primitives)
  const normalizeForCompare = (v) => {
    if (v === null || v === undefined) return "";
    if (typeof v === "object") {
      try {
        return JSON.stringify(v);
      } catch {
        return String(v);
      }
    }
    return String(v).trim();
  };

  const areEqual = (a, b) => normalizeForCompare(a) === normalizeForCompare(b);

  // union keys from both changeObj and oldObj
  const unionKeys = Array.from(
    new Set([...(changeObj ? Object.keys(changeObj) : []), ...(oldObj ? Object.keys(oldObj) : [])])
  );

  // Only show keys where old and new differ
  const diffKeys = unionKeys.filter((k) => !areEqual(changeObj?.[k], oldObj?.[k]));

  const formatKeyLabel = (k) =>
    k
      ? k
          .replace(/_/g, " ")
          .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1))
      : k;

  const handleExport = () => {
    let data = [];
    let fromName = roleReassignNames.oldUser || roleReassignData.oldUserId;
    let toName = roleReassignNames.newUser || roleReassignData.newUserId;
    let reqDate = formattedDateTime(ticketDetails?.create_at) || "-";
    let status = String(ticketDetails?.status || "").toUpperCase();
    let appDate = (status === "APPROVED" || status === "REJECTED") ? (formattedDateTime(ticketDetails?.update_at) || "-") : "-";

    const bpList = (roleReassignNames.bpNames && roleReassignNames.bpNames.length > 0) 
      ? roleReassignNames.bpNames 
      : (roleReassignData.bpIds && roleReassignData.bpIds.length > 0 ? roleReassignData.bpIds : []);
      
    if (bpList.length > 0) {
      bpList.forEach((bp, index) => {
        data.push({
          "Sr No": index + 1,
          "SM/CM/RM Name (From)": fromName,
          "BP Name": bp,
          "SM/CM/RM Name (To)": toName,
          "Request Date": reqDate,
          "Approvel Date (WEF)": appDate,
          "Status": status
        });
      });
    } else {
      let baNames = roleReassignNames.baNames && roleReassignNames.baNames.length > 0 
        ? roleReassignNames.baNames.join(", ") 
        : (roleReassignData.baIds && roleReassignData.baIds.length > 0 ? roleReassignData.baIds.join(", ") : "-");
        
      data.push({
        "Sr No": 1,
        "SM/CM/RM Name (From)": fromName,
        "BP Name": baNames !== "-" ? baNames : "-",
        "SM/CM/RM Name (To)": toName,
        "Request Date": reqDate,
        "Approvel Date (WEF)": appDate,
        "Status": status
      });
    }

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Role_Reassignment");
    XLSX.writeFile(wb, `Role_Reassignment_${ticketDetails?.ticketNumber || 'Export'}.xlsx`);
  };

  const handleExportBPs = () => {
    let data = [];
    const bpList = (roleReassignNames.bpNames && roleReassignNames.bpNames.length > 0) 
      ? roleReassignNames.bpNames 
      : (roleReassignData.bpIds && roleReassignData.bpIds.length > 0 ? roleReassignData.bpIds : []);
      
    bpList.forEach((bp, index) => {
      data.push({
        "Sr No": index + 1,
        "BP Name": bp
      });
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Selected_BPs");
    XLSX.writeFile(wb, `Selected_BPs_${ticketDetails?.ticketNumber || 'Export'}.xlsx`);
  };

  return (
    <Fragment>

      <Row>
        <Col sm="12">
          <Table className="custom-table table-sm">
            <tbody className="mtd-bg">
              <tr>
                <th>Ticket Number</th>
                <td>{ticketDetails?.ticketNumber ? ticketDetails?.ticketNumber : "-"}</td>
              </tr>
              <tr>
                <th>Ticket Type</th>
                <td>{getTitle(ticketDetails?.type) || "-"}</td>
              </tr>
              {ticketDetails?.created_reason && (
                <tr>
                  <th>Reason</th>
                  <td>{ticketDetails?.created_reason ? ticketDetails?.created_reason : "-"}</td>
                </tr>
              )}

              {isRoleReassignment ? (
                <Fragment>
                  <tr>
                    <th>Role Reassigned</th>
                    <td>{roleReassignData.roleKey}</td>
                  </tr>
                  <tr>
                    <th>Old User</th>
                    <td>{roleReassignNames.oldUser || roleReassignData.oldUserId}</td>
                  </tr>
                  <tr>
                    <th>New User</th>
                    <td>{roleReassignNames.newUser || roleReassignData.newUserId}</td>
                  </tr>
                  {Array.isArray(roleReassignData.bpIds) && roleReassignData.bpIds.length > 0 ? (
                    <tr>
                      <th>Selected BPs</th>
                      <td>
                        {roleReassignNames.bpNames.length > 0 
                          ? (roleReassignNames.bpNames.length > 5 
                              ? <>{roleReassignNames.bpNames.slice(0, 5).join(", ")}... <Button color="primary" size="sm" className="ml-2 py-0 px-2" style={{fontSize: '12px'}} onClick={handleExportBPs}>Export</Button></> 
                              : roleReassignNames.bpNames.join(", "))
                          : (roleReassignData.bpIds.length > 5
                              ? <>{roleReassignData.bpIds.slice(0, 5).join(", ")}... <Button color="primary" size="sm" className="ml-2 py-0 px-2" style={{fontSize: '12px'}} onClick={handleExportBPs}>Export</Button></> 
                              : roleReassignData.bpIds.join(", "))
                        }
                      </td>
                    </tr>
                  ) : null}
                  {Array.isArray(roleReassignData.baIds) && roleReassignData.baIds.length > 0 ? (
                    <tr>
                      <th>Selected BAs</th>
                      <td>{roleReassignNames.baNames.length > 0 ? roleReassignNames.baNames.join(", ") : roleReassignData.baIds.join(", ")}</td>
                    </tr>
                  ) : null}
                  <tr>
                    <th>Ticket Report</th>
                    <td>
                      <Button color="primary" size="sm" className="py-0 px-2" style={{fontSize: '12px'}} onClick={handleExport}>
                        Export
                      </Button>
                    </td>
                  </tr>
                </Fragment>
              ) : null}

              {isMscChangeTicket && (
                <tr>
                  <th>Old MSC %</th>
                  <td>
                    {parsedPayload?.reject
                      ? parsedPayload?.reject[0]?.Body?.msc_percentage == 0
                        ? ticketDetails?.partnership?.commission_percent
                        : parsedPayload?.reject[0]?.Body?.msc_percentage
                      : "-"}
                  </td>
                </tr>
              )}

              {isMscChangeTicket && (
                <tr>
                  <th>New MSC %</th>
                  <td>{parsedPayload?.accept ? parsedPayload?.accept[0]?.Body?.msc_percentage : "-"}</td>
                </tr>
              )}
              {isMscChangeTicket && (
                <tr>
                  <th>Plan</th>
                  <td>
                    {planDetails ? (
                      <div
                        onClick={() => setShowPlanDetailsModal(true)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          cursor: 'pointer',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          transition: 'all 0.2s ease',
                          color: '#3b82f6',
                          fontSize: '12px',
                          fontWeight: 500,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#eff6ff';
                          e.currentTarget.style.color = '#1e40af';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = '#3b82f6';
                        }}
                      >
                        <VisibilityIcon style={{ fontSize: '16px' }} />
                        View Plans
                      </div>
                    ) : (
                      parsedPayload?.accept ? parsedPayload?.accept[0]?.additionalFields?.planName : "-"
                    )}
                  </td>
                </tr>
              )}
              {isMscChangeTicket && (
                <tr>
                  <th>Approved By</th>
                  <td>{ticketDetails?.approved_by ? ticketDetails?.approved_by : "-"}</td>
                </tr>
              )}
              {!ticketDetails?.consolidator_id && !isMscChangeTicket && (
                  <>
                    <tr>
                      <th>BP Remark</th>
                      <td>{ticketDetails?.created_remark ? ticketDetails?.created_remark : "-"}</td>
                    </tr>
                    {module_id != 2 && (
                      <tr>
                        <th>Finance Remark</th>
                        <td>
                          {String(ticketDetails?.status || "").toUpperCase() == "REJECTED"
                            ? ticketDetails?.rejected_remark || "-"
                            : ticketDetails?.approved_remark || "-"}
                        </td>
                      </tr>
                    )}
                  </>
                )}
              <tr>
                <th>Created By</th>
                <td>
                  {ticketDetails?.created_by_name ? ticketDetails?.created_by_name : "-"} (
                  {ticketDetails?.created_by ? ticketDetails?.created_by : "-"})
                </td>
              </tr>
              {!ticketDetails?.consolidator_id && (
                <tr>
                  <th>Business Partner</th>
                  <td>
                    {ticketDetails?.partnership?.name ? ticketDetails?.partnership?.name : "-"} (
                    {ticketDetails?.partnership?.partnership_code ? ticketDetails?.partnership?.partnership_code : "-"})
                  </td>
                </tr>
              )}

              <tr>
                <th>Created Date</th>
                <td>{formattedDateTime(ticketDetails?.create_at) || "-"}</td>
              </tr>
              {String(ticketDetails?.status || '').toUpperCase() === "APPROVED" ||
              String(ticketDetails?.status || '').toUpperCase() === "REJECTED" ? (
                <tr>
                  <th>{String(ticketDetails?.status || '').toUpperCase() === "APPROVED" ? "Date Of Approval" : "Date Of Rejection"}</th>
                  <td>{formattedDateTime(ticketDetails?.update_at) || "-"}</td>
                </tr>
              ) : null}

              <tr>
                <th>Status</th>
                <td>
                  <span
                    className={(() => {
                      let badgeClass = "";
                      switch (String(ticketDetails?.status || '').toLowerCase()) {
                        case "initiated":
                          badgeClass = "badge badge-primary";
                          break;
                        case "in transit":
                          badgeClass = "badge badge-warning";
                          break;
                        case "approved":
                          badgeClass = "badge badge-success";
                          break;
                        case "rejected":
                          badgeClass = "badge badge-danger";
                          break;
                        default:
                          break;
                      }
                      return badgeClass;
                    })()}
                  >
                    {String(ticketDetails?.status || "").toUpperCase()}
                  </span>
                </td>
              </tr>
            </tbody>
          </Table>
        </Col>
      </Row>

      {/* Show changed values table for CASHIFY_POLICY_EDIT - only differences */}
      {ticketDetails?.type === "CASHIFY_POLICY_EDIT" && diffKeys.length > 0 && (
        <Row>
          <Col sm="12">
            <Table className="custom-table table-sm">
              <thead>
                <tr>
                  <th>Details</th>
                  <th>Old</th>
                  <th>New</th>
                </tr>
              </thead>
              <tbody className="mtd-bg">
                {diffKeys.map((key) => (
                  <tr key={key}>
                    <td style={{ textTransform: "capitalize" }}>{formatKeyLabel(key)}</td>
                    <td>{oldObj && oldObj[key] !== undefined && oldObj[key] !== null ? oldObj[key] : "-"}</td>
                    <td>{changeObj && changeObj[key] !== undefined && changeObj[key] !== null ? changeObj[key] : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Col>
        </Row>
      )}

      <PlanDetailsModal
        isOpen={showPlanDetailsModal}
        toggle={() => setShowPlanDetailsModal(false)}
        planDetails={planDetails}
      />
    </Fragment>
  );
};

export default TicketModal;